import DOMPurify from "dompurify";

/**
 * Replaces all line breaks (`\r\n`, `\r`, `\n`) and `<div></div>` tags with `<br />` tags and returns the new string
 *
 * Replaces all empty Paragraph tags `<p></p>` with `<br />` tags
 *
 * The result must not contain raw line breaks: the views render it with `white-space: pre-wrap`,
 * where a raw line break would show up as an additional empty line.
 *
 * This method was created for the migration/backwards compatibility of the old news text editor/value to the new RichTextEditor
 * @param stringToSanitize
 * @returns string
 */
export const getHtmlStringForPreview = (stringToSanitize: string | undefined | null) => {
    let sanitizedHtml = DOMPurify.sanitize(stringToSanitize ?? "");
    if (sanitizedHtml.length > 0) {
        sanitizedHtml = sanitizedHtml.replace(/<div>|<\/div>|\r\n|\r|\n/gim, (substring: string) =>
            substring === "<div>" ? "" : "<br />"
        );
        sanitizedHtml = sanitizedHtml.replaceAll("<p></p>", "<br />");
    }
    return sanitizedHtml;
};

/**
 * Prepares HTML content for loading into a TipTap RichTextEditor.
 *
 * Calls {@link getHtmlStringForPreview} first for legacy compatibility, then converts
 * `<br />` tags between, before, and after block-level elements
 * (`<p>`, `<ul>`, `<ol>`, `<h1>`–`<h6>`, `<blockquote>`) back to empty paragraphs `<p></p>`,
 * which is TipTap's native representation for empty lines (Enter key).
 *
 * `<br />` tags inside text are left untouched. TipTap parses them as hard breaks, so the
 * editor shows the same line breaks as the preview and the coowner view.
 */
export const getHtmlStringForTipTap = (stringToSanitize?: string | undefined | null) => {
    let sanitizedPreviewText = getHtmlStringForPreview(stringToSanitize);

    if (sanitizedPreviewText.length === 0) {
        return sanitizedPreviewText;
    }

    const blockTags = "p|ul|ol|h[1-6]|blockquote";

    // Convert <br /> between block-level elements to empty <p></p> paragraphs.
    // TipTap represents empty lines (Enter) as <p></p>, not <br /> between blocks.
    sanitizedPreviewText = sanitizedPreviewText.replace(
        new RegExp(`(<\\/(?:${blockTags})>)((?:\\s*<br\\s*\\/?>)+)(\\s*<(?:${blockTags})[\\s>])`, "gim"),
        (_, close, brs, open) => {
            const count = (brs.match(/<br\s*\/?>/gi) || []).length;
            return close + "<p></p>".repeat(count) + open;
        }
    );

    // Handle trailing <br /> after the last block element
    sanitizedPreviewText = sanitizedPreviewText.replace(
        new RegExp(`(<\\/(?:${blockTags})>)((?:\\s*<br\\s*\\/?>)+)\\s*$`, "gim"),
        (_, close, brs) => {
            const count = (brs.match(/<br\s*\/?>/gi) || []).length;
            return close + "<p></p>".repeat(count);
        }
    );

    // Handle leading <br /> before the first block element
    sanitizedPreviewText = sanitizedPreviewText.replace(
        new RegExp(`^((?:<br\\s*\\/?>\\s*)+)(<(?:${blockTags})[\\s>])`, "gim"),
        (_, brs, open) => {
            const count = (brs.match(/<br\s*\/?>/gi) || []).length;
            return "<p></p>".repeat(count) + open;
        }
    );

    return sanitizedPreviewText;
};
